import { useCallback, useEffect, useRef, useState } from "react";
import { useDataChangedTick } from "../useDataChanged";
import { AppShell } from "../components/AppShell";
import { OverviewTopbar, ProjectsRow } from "../components/OverviewTopbar";
import { ScanStrip } from "../components/ScanStrip";
import {
  api,
  isProjectVerified,
  normalizeApiText,
  type ScanEvent,
} from "../api";
import { useAppState } from "../AppStateContext";
import { useI18n } from "../i18n";
import "./overview.css";
import "./feed.css";

const PAGE_SIZE = 30;
const POLL_INTERVAL_MS = 5_000;
const NEW_HIGHLIGHT_MS = 1_500;

function sortDesc(events: ScanEvent[]): ScanEvent[] {
  return [...events].sort((a, b) => {
    const ta = new Date(a.timestamp).getTime();
    const tb = new Date(b.timestamp).getTime();
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
    // Fall back to sequence_number when timestamps tie or are unparseable.
    return (b.sequence_number ?? "").localeCompare(a.sequence_number ?? "");
  });
}

export default function FeedPage() {
  const { projects, activeProject, setActiveProjectId, activeScan, viewedScan } = useAppState();
  const dataTick = useDataChangedTick();
  const { t } = useI18n();
  const displayScan = viewedScan ?? activeScan;
  const activeScanId = displayScan?.scan_id ?? null;

  const [events, setEvents] = useState<ScanEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newIds, setNewIds] = useState<Set<string>>(new Set());

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const newIdsTimeoutRef = useRef<number | null>(null);

  const markNew = useCallback((ids: string[]) => {
    setNewIds(new Set(ids));
    if (newIdsTimeoutRef.current) window.clearTimeout(newIdsTimeoutRef.current);
    newIdsTimeoutRef.current = window.setTimeout(() => {
      setNewIds(new Set());
      newIdsTimeoutRef.current = null;
    }, NEW_HIGHLIGHT_MS);
  }, []);

  // Reset everything when the active scan changes.
  useEffect(() => {
    setEvents([]);
    setNextCursor(null);
    setHasMore(false);
    setError(null);
    setNewIds(new Set());
    if (newIdsTimeoutRef.current) {
      window.clearTimeout(newIdsTimeoutRef.current);
      newIdsTimeoutRef.current = null;
    }
  }, [activeScanId]);

  // Initial load (newest page).
  useEffect(() => {
    if (!activeScanId) return;
    let cancelled = false;
    setInitialLoading(true);
    (async () => {
      try {
        const res = await api.scanEvents(activeScanId, { page_size: PAGE_SIZE });
        if (cancelled) return;
        setEvents(sortDesc(res.events));
        setNextCursor(res.next_cursor || null);
        setHasMore(Boolean(res.has_more));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed");
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeScanId, dataTick]);

  // Poll the newest page periodically; prepend any events we haven't seen.
  useEffect(() => {
    if (!activeScanId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.scanEvents(activeScanId, { page_size: PAGE_SIZE });
        if (cancelled) return;
        setEvents((prev) => {
          const known = new Set(prev.map((e) => e.event_id));
          const incoming = res.events.filter((e) => !known.has(e.event_id));
          if (incoming.length === 0) return prev;
          markNew(incoming.map((e) => e.event_id));
          return sortDesc([...incoming, ...prev]);
        });
      } catch {
        /* swallow — surfaced on initial load */
      }
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [activeScanId, markNew, dataTick]);

  // IntersectionObserver: load older events when the sentinel becomes visible.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !activeScanId || !hasMore || !nextCursor) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (loadingMore) return;
        setLoadingMore(true);
        (async () => {
          try {
            const res = await api.scanEvents(activeScanId, {
              cursor: nextCursor,
              page_size: PAGE_SIZE,
            });
            setEvents((prev) => {
              const known = new Set(prev.map((e) => e.event_id));
              const additional = res.events.filter((e) => !known.has(e.event_id));
              return sortDesc([...prev, ...additional]);
            });
            setNextCursor(res.next_cursor || null);
            setHasMore(Boolean(res.has_more));
          } catch (err) {
            setError(err instanceof Error ? err.message : "Failed");
          } finally {
            setLoadingMore(false);
          }
        })();
      },
      { rootMargin: "240px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeScanId, hasMore, nextCursor, loadingMore]);

  useEffect(() => {
    return () => {
      if (newIdsTimeoutRef.current) window.clearTimeout(newIdsTimeoutRef.current);
    };
  }, []);

  return (
    <AppShell>
      <div className="overview anim-page">
        <OverviewTopbar />
        <ProjectsRow
          searches={projects.map((p) => ({
            id: p.project_id,
            label: p.name,
            verified: isProjectVerified(p),
          }))}
          activeId={activeProject?.project_id}
          onSelect={setActiveProjectId}
        />
        <ScanStrip />

        <p className="overview__section-label">{t("feed.sectionLabel")}</p>

        {error ? <p className="auth-error">{error}</p> : null}

        <div className="feed">
          {initialLoading && events.length === 0 ? (
            <FeedSkeleton />
          ) : events.length === 0 ? (
            <p className="helper" style={{ padding: 20 }}>{t("feed.empty")}</p>
          ) : (
            events.map((r) => {
              const tag = r.phase ?? r.node_type ?? r.event_type ?? "";
              const isNew = newIds.has(r.event_id);
              return (
                <div
                  key={r.event_id}
                  className={`feed__row${isNew ? " feed__row--new" : ""}`}
                >
                  <span className="row__time">{formatEventTime(r.timestamp)}</span>
                  <span className={`row__stage row__stage--${stageColor(tag)}`}>
                    {normalizeApiText(tag).toUpperCase()}
                  </span>
                  <span className="row__label">{r.message}</span>
                </div>
              );
            })
          )}
        </div>

        {events.length > 0 && hasMore ? (
          <div ref={sentinelRef} className="feed__sentinel">
            <span className="feed__sentinel-spinner" aria-hidden />
            {t("feed.loadingMore")}
          </div>
        ) : null}

        {events.length > 0 ? (
          <div className="feed__footer">
            <span>{t("feed.counter", { events: events.length })}</span>
            {!hasMore ? <span>{t("feed.end")}</span> : null}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}

function FeedSkeleton() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="skeleton skeleton-row" style={{ width: `${85 - (i % 4) * 8}%` }} />
      ))}
    </>
  );
}

function stageColor(stage: string): string {
  switch (normalizeApiText(stage)) {
    case "exploit":
      return "red";
    case "rbc":
      return "orange";
    case "scanner":
      return "blue";
    case "recon":
      return "purple";
    default:
      return "blue";
  }
}

function formatEventTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return ts;
  }
}
